import { Command } from "commander";
import { readFileSync, writeFileSync, existsSync, mkdirSync, readdirSync, statSync } from "node:fs";
import { resolve, basename, dirname } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import { input, confirm, select } from "@inquirer/prompts";
import { loadData, isOllamaAvailable } from "@fathom/core";
import { extractWorkItems, enrichSlices, formatAsMarkdown, formatSliceTable } from "@fathom/intake";
import type { IntakeResult } from "@fathom/intake";
import { syncIntake, logConvexStatus } from "../convex-client.js";
import { getProjectName, getIntakeDir, updateProjectConfig } from "../project-config.js";
import { withSpinner } from "../ux/spinner.js";
import { fatalError } from "../ux/errors.js";

/**
 * SEC-02: Check that a resolved path is within the given boundary directory.
 * Prevents path traversal attacks via user-supplied file paths.
 */
function isWithinBoundary(resolvedPath: string, boundary: string): boolean {
  return resolvedPath.startsWith(boundary);
}

/**
 * Find markdown files in cwd (non-recursive, skipping node_modules/dotfiles).
 * Returns relative paths sorted by modification time (newest first).
 */
function findLocalMarkdownFiles(): string[] {
  try {
    const cwd = process.cwd();
    return readdirSync(cwd)
      .filter((f) => /\.(md|txt|mdx)$/i.test(f) && !f.startsWith("."))
      .map((f) => ({ name: f, mtime: statSync(resolve(cwd, f)).mtimeMs }))
      .sort((a, b) => b.mtime - a.mtime)
      .map((f) => f.name);
  } catch {
    return [];
  }
}

/**
 * Core intake pipeline: resolve input → extract → enrich → save spec → populate registry.
 * Used by both `fathom intake` (standalone) and `fathom go` (workflow).
 * Returns the IntakeResult and spec file path.
 */
export async function runIntakeFlow(
  projectName: string,
): Promise<{ result: IntakeResult; specPath: string }> {
  const cwd = process.cwd();

  // ── Resolve input interactively ──
  const { rawInput, source } = await resolveInput(cwd);

  // ── Check for LLM provider ──
  const hasAnthropicKey = !!process.env.ANTHROPIC_API_KEY;
  const hasOpenAIKey = !!process.env.OPENAI_API_KEY;

  let provider: 'anthropic' | 'openai' | 'ollama';
  if (hasAnthropicKey) {
    provider = 'anthropic';
  } else if (hasOpenAIKey) {
    provider = 'openai';
  } else if (await isOllamaAvailable()) {
    provider = 'ollama';
  } else {
    fatalError(
      "No LLM provider available",
      "Intake requires an LLM — no API key set and Ollama not running",
      "Install Ollama (https://ollama.ai) or export ANTHROPIC_API_KEY / OPENAI_API_KEY",
    );
  }

  // ── Extract ──
  const providerLabel = provider === 'anthropic' ? 'Claude' : provider === 'openai' ? 'OpenAI' : 'Ollama (local)';
  let items;
  try {
    items = await withSpinner(`Extracting work items via ${providerLabel}...`, async () =>
      extractWorkItems(rawInput, {
        provider,
        ...(provider === 'openai' ? { openaiApiKey: process.env.OPENAI_API_KEY } : {}),
      })
    );
  } catch (err) {
    // SEC-05: Mask any API keys that may appear in error messages
    let errMsg = (err as Error).message;
    for (const envKey of ['ANTHROPIC_API_KEY', 'OPENAI_API_KEY']) {
      const val = process.env[envKey];
      if (val && errMsg.includes(val)) {
        errMsg = errMsg.replaceAll(val, val.length <= 6 ? '***' : val.slice(0, 3) + '***' + val.slice(-3));
      }
    }
    fatalError("Extraction failed", errMsg, "Check your API key and try again");
  }

  if (items.length === 0) {
    console.log(chalk.yellow("  No actionable work items found in input."));
    process.exit(0);
  }

  console.log(chalk.green(`  ✓ Extracted ${items.length} work item${items.length === 1 ? "" : "s"}`));

  // ── Enrich ──
  console.log(chalk.dim("  Enriching with estimates..."));
  const data = loadData();
  const slices = enrichSlices(items, { data, project: projectName });

  const totalTokens = slices.reduce((s, sl) => s + sl.estimatedTokens, 0);
  const totalCost = slices.reduce((s, sl) => s + sl.estimatedCost, 0);

  const result: IntakeResult = {
    project: projectName,
    source,
    rawInput,
    slices,
    totalEstimatedTokens: totalTokens,
    totalEstimatedCost: totalCost,
    createdAt: new Date().toISOString(),
  };

  // ── Display table ──
  displayIntakeTable(projectName, slices, totalTokens, totalCost);

  // ── Save spec to default location ──
  const intakeDir = getIntakeDir();
  const datestamp = new Date().toISOString().slice(0, 10);
  const specFile = `${intakeDir}/${projectName}-intake-${datestamp}.md`;
  const specPath = resolve(specFile);

  mkdirSync(dirname(specPath), { recursive: true });
  writeFileSync(specPath, formatAsMarkdown(result));
  console.log(chalk.green(`\n  ✓ Spec saved to ${specFile}`));

  // ── Populate registry ──
  writeRegistry(projectName, slices);

  // ── Sync to Convex ──
  await doConvexSync(result, rawInput, source, projectName);

  return { result, specPath };
}

/**
 * Resolve raw input interactively: discover files, prompt user.
 */
async function resolveInput(
  cwd: string,
): Promise<{ rawInput: string; source: string }> {
  const localFiles = findLocalMarkdownFiles();

  if (localFiles.length > 0) {
    const fileChoices = localFiles.slice(0, 8).map((f) => ({
      name: f,
      value: f,
    }));
    fileChoices.push(
      { name: "Other file...", value: "__other__" },
      { name: "Type/paste text instead", value: "__text__" },
    );

    const chosen = await select({
      message: `Input source (${localFiles.length} file${localFiles.length === 1 ? "" : "s"} found in ${basename(cwd)}):`,
      choices: fileChoices,
    });

    if (chosen === "__text__") {
      return {
        rawInput: await input({ message: "Paste your feedback / requirements:" }),
        source: "text",
      };
    } else if (chosen === "__other__") {
      const filePath = await input({ message: "Path to input file:", default: cwd + "/" });
      const resolved = resolve(filePath.trim());
      if (!isWithinBoundary(resolved, cwd)) {
        fatalError("Path traversal rejected", "File must be within project directory", "Use a relative path within the project");
      }
      if (!existsSync(resolved)) {
        fatalError("File not found", resolved + " does not exist", "Check the file path");
      }
      return { rawInput: readFileSync(resolved, "utf-8"), source: "file" };
    } else {
      return { rawInput: readFileSync(resolve(chosen), "utf-8"), source: "file" };
    }
  }

  // No markdown files found
  const inputType = await select({
    message: "How would you like to provide input?",
    choices: [
      { name: "Enter a file path", value: "file" },
      { name: "Type/paste text", value: "text" },
    ],
  });

  if (inputType === "file") {
    const filePath = await input({ message: "Path to input file:", default: cwd + "/" });
    const resolved = resolve(filePath.trim());
    if (!isWithinBoundary(resolved, cwd)) {
      fatalError("Path traversal rejected", "File must be within project directory", "Use a relative path within the project");
    }
    if (!existsSync(resolved)) {
      fatalError("File not found", resolved + " does not exist", "Check the file path");
    }
    return { rawInput: readFileSync(resolved, "utf-8"), source: "file" };
  }

  return {
    rawInput: await input({ message: "Paste your feedback / requirements:" }),
    source: "text",
  };
}

/**
 * Display the intake results as a table.
 */
export function displayIntakeTable(
  projectName: string,
  slices: IntakeResult["slices"],
  totalTokens: number,
  totalCost: number,
): void {
  console.log(chalk.bold(`\n  Intake Analysis: ${projectName}`));
  console.log(chalk.dim("  " + "─".repeat(58)));

  const tableRows = formatSliceTable(slices);
  const table = new Table({
    head: [
      chalk.white("#"),
      chalk.white("Name"),
      chalk.white("Cat."),
      chalk.white("Size"),
      chalk.white("Pri."),
      chalk.white("Tokens"),
      chalk.white("Cost"),
      chalk.white("Benchmark"),
    ],
    colAligns: ["right", "left", "left", "center", "center", "right", "right", "left"],
  });

  tableRows.forEach((row, i) => {
    table.push([
      String(i + 1),
      row.name,
      row.category,
      row.complexity,
      row.priority,
      row.tokens,
      row.cost,
      row.benchmark,
    ]);
  });

  console.log(table.toString());
  console.log(
    chalk.bold(`\n  Total: ${totalTokens.toLocaleString()} tokens — $${totalCost.toFixed(2)}`),
  );
}

export const intakeCommand = new Command("intake")
  .description("Extract work items from raw feedback, bug reports, or meeting notes")
  .argument("[text]", "Raw text input to analyze")
  .option("-f, --file <path>", "Read input from a file")
  .option("-p, --project <name>", "Project name")
  .option("-o, --output <path>", "Write markdown spec to file")
  .option("--json", "Output as JSON")
  .option("--markdown", "Output as markdown to stdout")
  .option("--provider <provider>", "LLM provider (anthropic, openai, ollama, auto)", "auto")
  .action(async (text: string | undefined, options: {
    file?: string;
    project?: string;
    output?: string;
    json?: boolean;
    markdown?: boolean;
    provider?: string;
  }) => {
    try {
    const cwd = process.cwd();

    // ── Resolve project name: flag > config > prompt ──
    let projectName = options.project ?? getProjectName();
    if (projectName === "unnamed") {
      projectName = await input({
        message: "Project name:",
        default: basename(cwd),
      });
      updateProjectConfig({ project: projectName });
    }

    // ── Resolve input: flag > argument > interactive ──
    let rawInput: string;
    let source: string;

    if (options.file) {
      const filePath = resolve(options.file);
      if (!isWithinBoundary(filePath, cwd)) {
        fatalError("Path traversal rejected", "File must be within project directory", "Use a relative path within the project");
      }
      if (!existsSync(filePath)) {
        fatalError("File not found", filePath + " does not exist", "Check the file path");
      }
      rawInput = readFileSync(filePath, "utf-8");
      source = "file";
    } else if (text) {
      rawInput = text;
      source = "text";
    } else {
      // Discover markdown files in cwd to offer as choices
      const localFiles = findLocalMarkdownFiles();

      if (localFiles.length > 0) {
        const fileChoices = localFiles.slice(0, 8).map((f) => ({
          name: f,
          value: f,
        }));
        fileChoices.push(
          { name: "Other file...", value: "__other__" },
          { name: "Type/paste text instead", value: "__text__" },
        );

        const chosen = await select({
          message: `Input source (${localFiles.length} file${localFiles.length === 1 ? "" : "s"} found in ${basename(cwd)}):`,
          choices: fileChoices,
        });

        if (chosen === "__text__") {
          rawInput = await input({
            message: "Paste your feedback / requirements:",
          });
          source = "text";
        } else if (chosen === "__other__") {
          const filePath = await input({
            message: "Path to input file:",
            default: cwd + "/",
          });
          const resolved = resolve(filePath.trim());
          if (!isWithinBoundary(resolved, cwd)) {
            fatalError("Path traversal rejected", "File must be within project directory", "Use a relative path within the project");
          }
          if (!existsSync(resolved)) {
            fatalError("File not found", resolved + " does not exist", "Check the file path");
          }
          rawInput = readFileSync(resolved, "utf-8");
          source = "file";
        } else {
          rawInput = readFileSync(resolve(chosen), "utf-8");
          source = "file";
        }
      } else {
        // No markdown files found — ask for path or text
        const inputType = await select({
          message: "How would you like to provide input?",
          choices: [
            { name: "Enter a file path", value: "file" },
            { name: "Type/paste text", value: "text" },
          ],
        });

        if (inputType === "file") {
          const filePath = await input({
            message: "Path to input file:",
            default: cwd + "/",
          });
          const resolved = resolve(filePath.trim());
          if (!isWithinBoundary(resolved, cwd)) {
            fatalError("Path traversal rejected", "File must be within project directory", "Use a relative path within the project");
          }
          if (!existsSync(resolved)) {
            fatalError("File not found", resolved + " does not exist", "Check the file path");
          }
          rawInput = readFileSync(resolved, "utf-8");
          source = "file";
        } else {
          rawInput = await input({
            message: "Paste your feedback / requirements:",
          });
          source = "text";
        }
      }
    }

    // ── Check for LLM provider ──
    const hasAnthropicKey = !!process.env.ANTHROPIC_API_KEY;
    const hasOpenAIKey = !!process.env.OPENAI_API_KEY;

    let provider: 'anthropic' | 'openai' | 'ollama';
    if (options.provider && options.provider !== "auto") {
      provider = options.provider as 'anthropic' | 'openai' | 'ollama';
    } else {
      if (hasAnthropicKey) {
        provider = 'anthropic';
      } else if (hasOpenAIKey) {
        provider = 'openai';
      } else if (await isOllamaAvailable()) {
        provider = 'ollama';
      } else {
        fatalError(
          "No LLM provider available",
          "Intake requires an LLM — no API key set and Ollama not running",
          "Install Ollama (https://ollama.ai) or export ANTHROPIC_API_KEY / OPENAI_API_KEY",
        );
      }
    }

    // ── Step 1: Extract ──
    const providerLabel = provider === 'anthropic' ? 'Claude' : provider === 'openai' ? 'OpenAI' : 'Ollama (local)';
    let items;
    try {
      items = await withSpinner(`Extracting work items via ${providerLabel}...`, async () =>
        extractWorkItems(rawInput, {
          provider,
          ...(provider === 'openai' ? { openaiApiKey: process.env.OPENAI_API_KEY } : {}),
        })
      );
    } catch (err) {
      // SEC-05: Mask any API keys that may appear in error messages
      let errMsg = (err as Error).message;
      for (const envKey of ['ANTHROPIC_API_KEY', 'OPENAI_API_KEY']) {
        const val = process.env[envKey];
        if (val && errMsg.includes(val)) {
          errMsg = errMsg.replaceAll(val, val.length <= 6 ? '***' : val.slice(0, 3) + '***' + val.slice(-3));
        }
      }
      fatalError("Extraction failed", errMsg, "Check your API key and try again");
    }

    if (items.length === 0) {
      console.log(chalk.yellow("No actionable work items found in input."));
      process.exit(0);
    }

    console.log(chalk.green(`Extracted ${items.length} work item${items.length === 1 ? "" : "s"}`));

    // ── Step 2: Enrich ──
    console.log(chalk.dim("Enriching with estimates..."));
    const data = loadData();
    const slices = enrichSlices(items, { data, project: projectName });

    const totalTokens = slices.reduce((s, sl) => s + sl.estimatedTokens, 0);
    const totalCost = slices.reduce((s, sl) => s + sl.estimatedCost, 0);

    const result: IntakeResult = {
      project: projectName,
      source,
      rawInput,
      slices,
      totalEstimatedTokens: totalTokens,
      totalEstimatedCost: totalCost,
      createdAt: new Date().toISOString(),
    };

    // ── Populate registry for tracking ──
    writeRegistry(projectName, slices);

    // ── Step 3: Output ──

    // JSON mode: print and exit
    if (options.json) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    // Direct file output via -o flag
    if (options.output) {
      const outPath = resolve(options.output);
      mkdirSync(dirname(outPath), { recursive: true });
      writeFileSync(outPath, formatAsMarkdown(result));
      console.log(chalk.green(`\nSpec written to ${outPath}`));
      await doConvexSync(result, rawInput, source, projectName);
      return;
    }

    // Markdown to stdout
    if (options.markdown) {
      console.log(formatAsMarkdown(result));
      await doConvexSync(result, rawInput, source, projectName);
      return;
    }

    // ── Default: table display + interactive save ──
    console.log(chalk.bold(`\nFathom — Intake Analysis: ${projectName}`));
    console.log(chalk.dim("─".repeat(60)));

    const tableRows = formatSliceTable(slices);
    const table = new Table({
      head: [
        chalk.white("#"),
        chalk.white("Name"),
        chalk.white("Cat."),
        chalk.white("Size"),
        chalk.white("Pri."),
        chalk.white("Tokens"),
        chalk.white("Cost"),
        chalk.white("Benchmark"),
      ],
      colAligns: ["right", "left", "left", "center", "center", "right", "right", "left"],
    });

    tableRows.forEach((row, i) => {
      table.push([
        String(i + 1),
        row.name,
        row.category,
        row.complexity,
        row.priority,
        row.tokens,
        row.cost,
        row.benchmark,
      ]);
    });

    console.log(table.toString());
    console.log(
      chalk.bold(`\nTotal: ${totalTokens.toLocaleString()} tokens — $${totalCost.toFixed(2)}`),
    );

    // ── Interactive: ask where to save ──
    const intakeDir = getIntakeDir();
    const datestamp = new Date().toISOString().slice(0, 10);
    const defaultFile = `${intakeDir}/${projectName}-intake-${datestamp}.md`;

    const saveAction = await select({
      message: "What would you like to do with this spec?",
      choices: [
        { name: `Save to ${defaultFile}`, value: "default" },
        { name: "Save to a custom location", value: "custom" },
        { name: "Print markdown to console", value: "print" },
        { name: "Skip (don't save)", value: "skip" },
      ],
    });

    if (saveAction === "default" || saveAction === "custom") {
      let outPath: string;

      if (saveAction === "custom") {
        const customPath = await input({
          message: "Output file path:",
          default: defaultFile,
        });
        outPath = resolve(customPath);
      } else {
        outPath = resolve(defaultFile);
      }

      mkdirSync(dirname(outPath), { recursive: true });
      writeFileSync(outPath, formatAsMarkdown(result));
      console.log(chalk.green(`\nSpec written to ${outPath}`));

      // Remember custom directory for next time
      const chosenDir = dirname(outPath);
      const resolvedDefaultDir = resolve(intakeDir);
      if (chosenDir !== resolvedDefaultDir) {
        const remember = await confirm({
          message: `Use "${dirname(outPath).replace(resolve(".") + "/", "")}" as default output directory next time?`,
          default: true,
        });
        if (remember) {
          const relativeDir = dirname(outPath).replace(resolve(".") + "/", "");
          updateProjectConfig({ intakeDir: relativeDir });
          console.log(chalk.dim(`  Saved to .fathom/config.json`));
        }
      }
    } else if (saveAction === "print") {
      console.log("\n" + formatAsMarkdown(result));
    }

    // Sync to Convex
    await doConvexSync(result, rawInput, source, projectName);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
        console.log(chalk.dim("\n  Exiting.\n"));
        return;
      }
      throw err;
    }
  });

/**
 * Generate and write the feature registry from intake slices.
 * Populates .fathom/registry.json so tracking can auto-tag sessions.
 */
function writeRegistry(
  projectName: string,
  slices: IntakeResult["slices"],
): void {
  const fathomDir = resolve(process.cwd(), ".fathom");
  const registryPath = resolve(fathomDir, "registry.json");

  // Build registry entries from enriched slices
  const features = slices.map((s) => {
    const keywords = new Set<string>();
    for (const word of s.name.toLowerCase().split(/\s+/)) {
      if (word.length > 2) keywords.add(word);
    }
    for (const task of s.aiTasks) {
      for (const word of task.toLowerCase().split(/[-_\s]+/)) {
        if (word.length > 2) keywords.add(word);
      }
    }
    keywords.add(s.category.toLowerCase());

    const slug = s.sliceId;
    return {
      id: s.sliceId,
      name: s.name,
      keywords: Array.from(keywords),
      file_patterns: [`src/**/*${slug}*`, `src/${slug}/**`],
      git_branch_patterns: [`feature/${slug}*`, `feat/*${slug}*`],
      estimated_tokens: s.estimatedTokens,
      status: "not-started" as const,
    };
  });

  // Merge with existing registry if present
  let existing: typeof features = [];
  if (existsSync(registryPath)) {
    try {
      const data = JSON.parse(readFileSync(registryPath, "utf-8"));
      if (Array.isArray(data.features)) {
        const newIds = new Set(features.map((f) => f.id));
        existing = data.features.filter(
          (f: { id: string }) => !newIds.has(f.id),
        );
      }
    } catch {
      // overwrite if corrupt
    }
  }

  const registry = {
    project: projectName,
    features: [...existing, ...features],
    generatedAt: new Date().toISOString(),
  };

  mkdirSync(fathomDir, { recursive: true });
  writeFileSync(registryPath, JSON.stringify(registry, null, 2) + "\n");
  console.log(
    chalk.green(`  Registry updated: ${features.length} features → .fathom/registry.json`),
  );
}

async function doConvexSync(
  result: IntakeResult,
  rawInput: string,
  source: string,
  projectName: string,
): Promise<void> {
  const synced = await syncIntake({
    projectName,
    rawInput,
    source,
    extractedSlices: result.slices.map((s) => ({
      sliceId: s.sliceId,
      name: s.name,
      category: s.category,
      complexity: s.complexity,
      priority: s.priority,
      description: s.description,
      estimatedTokens: s.estimatedTokens,
      estimatedCost: s.estimatedCost,
      velocityBenchmark: s.velocityBenchmark,
      status: s.status,
    })),
  });
  logConvexStatus(synced);
}
